/*
 * TEABALLS · Black Month 2026 — collection quick add for configurator products.
 * Loaded by snippets/teaballs-bm-head.liquid on collection templates only.
 *
 * Products whose page uses the Kaufkonfigurator block (blocks/tb-purchase-configurator.liquid,
 * e.g. the 3er sets) have no regular buy-buttons block. Horizon's quick-add modal shows only
 * an allow-list of blocks, so their modal stayed empty apart from the title.
 * When such a product opens in the quick-add modal, this script
 *  1. marks the configurator cell with "buy-buttons-block", so the modal's allow-list keeps it, and
 *  2. runs the block's own module script once if <tb-purchase-configurator> is not defined yet
 *     (scripts inside fetched modal HTML never execute on their own).
 * On phones Horizon builds a compact modal and only moves the first <product-form-component> across,
 * which for these products is the configurator's inner form without its <tb-purchase-configurator>
 * wrapper (unstyled and lifeless). There the script loads the product page once and puts the complete
 * configurator cell in place of that orphaned form.
 * The configurator then works exactly as on the product page (format, quantity, mode, cart).
 *
 * Poster title + flavour pills: the long product title ("TEABALLS - 3er Set FRUIT MIX - KIRSCHE,
 * WILDBERRY, ERDBEERE") is shown as a stamp ("3ER SET") plus the set name ("FRUIT MIX."), and the
 * flavours become pills. Theme settings › Black Month · Produktseite & Karten › "Sorten im
 * Schnellkauf" picks pills with the flavour photo (taken from the single-flavour products of the
 * collections "teaballs" and "teaballs-bio") or name-only pills. Titles that don't follow the pattern
 * stay as they are; the full title stays the link's accessible name.
 *
 * Product cards (collection grids and Horizon product lists such as the BFCM hub) get the same
 * treatment: stamp + poster name from the card's own text blocks, and the
 * flavour line ("Kirsche | Wildberry | Erdbeere") as pills (sets with photos per the setting above;
 * ingredient lists of single flavours name-only). The original blocks stay in the markup and are only
 * hidden by the stylesheet once a card has been built, so without this script nothing is lost.
 *
 * Also loaded on the Wunschmix page (page.wunschmix): its flavour cards (.tb13-card, server-rendered by
 * sections/ss-bundle-builder-12-section-fixed.liquid) get the same stamp + poster name; a flavour pair
 * in the title ("SÜSSE LIEBE - HIMBEERE & VANILLE") becomes name-only pills. Styles live in
 * teaballs-bm-wunschmix.css. The builder's search and filters read product data, not the card text.
 * The page heading ("Dein Tee. Dein Mix.") becomes a poster: first sentence as stamp, the rest big.
 *
 * Product pages (all product templates except Advent and SimsalaGrimm, see teaballs-bm-head.liquid):
 * the title block of the main product section gets the same stamp + poster name, and sets show
 * their flavours as pills (photo or name only, same setting). Styles: teaballs-bm-product.css.
 */
(() => {
  const MODAL_CONTENT_ID = 'quick-add-modal-content';
  const ELEMENT = 'tb-purchase-configurator';
  let definitionRequested = false;

  const defineConfigurator = (cell) => {
    if (definitionRequested || customElements.get(ELEMENT)) return;
    const source = Array.from(cell.querySelectorAll('script[type="module"]')).find((script) =>
      script.textContent.includes(`customElements.define('${ELEMENT}'`)
    );
    if (!source) return;
    definitionRequested = true;
    const script = document.createElement('script');
    script.type = 'module';
    script.textContent = source.textContent;
    document.head.appendChild(script);
  };

  const configuratorCell = (scope) => scope.querySelector(ELEMENT)?.closest('.tbp-cell') || null;

  /* desktop: full product details are in the modal, only the allow-list hides the configurator */
  const prepareDetails = (root) => {
    const details = root.querySelector('.product-details');
    if (!details) return;
    const cell = configuratorCell(details);
    if (!cell || cell.classList.contains('buy-buttons-block')) return;
    if (details.querySelector('.buy-buttons-block')) return;
    cell.classList.add('buy-buttons-block', 'tb-qa-configurator');
    defineConfigurator(cell);
  };

  /* phones: replace the orphaned inner form with the complete configurator from the product page */
  const repairCompact = async (root) => {
    const orphan = root.querySelector(':scope > product-form-component.tbp__product-form');
    const link = root.querySelector('.product-header a[href]');
    if (!orphan || !link || orphan.dataset.tbQaRepair) return;
    orphan.dataset.tbQaRepair = 'pending';
    orphan.hidden = true;
    try {
      const response = await fetch(link.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cell = configuratorCell(page);
      if (!cell || !orphan.isConnected) throw new Error('no configurator');
      const node = document.importNode(cell, true);
      node.classList.add('tb-qa-configurator');
      defineConfigurator(node);
      orphan.replaceWith(node);
    } catch (error) {
      orphan.hidden = false;
      orphan.dataset.tbQaRepair = 'failed';
    }
  };

  /* ---------- poster title + flavour pills ---------- */
  const config = window.TbBmQuickAdd || {};
  const FLAVOR_STYLE = config.flavorStyle === 'text' ? 'text' : 'image';
  const FLAVOR_SOURCES = ['teaballs', 'teaballs-bio'];
  const CACHE_KEY = 'tb-bm-qa-flavors-ingredient-v2';
  const CACHE_TTL = 6 * 60 * 60 * 1000;

  const squash = (text) => text.replace(/\s+/g, ' ').trim();
  /* comparable key: upper case, dashes/ampersands as spaces */
  const keyOf = (text) => squash(text.toUpperCase().replace(/[-–—_/&+]/g, ' ').replace(/[^\p{L}\p{N} ]/gu, ''));
  const BRAND = /^TEABALLS(\s+BIO)?(?:\s+x\s+((?:Kinder\s+)?[\p{L}\p{N}-]+))?\b\s*/iu;
  const SET = /\bset\b/iu;
  const SET_WORDS = /\b(?:(\d+)\s*er|(mini|trio))?[\s-]*(?:tee[\s-]*)?set\b/iu;
  const COUNT = /\b(\d+)\s*er\b[\s-]*/iu;

  const titleCase = (text) => {
    if (/\p{Ll}/u.test(text)) return text;
    return text.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (match, sep, letter) => sep + letter.toUpperCase());
  };

  /** @returns {{ kicker: string, name: string, sub: string, flavors: string[], bio: boolean, partner: string } | null} */
  const parseTitle = (raw) => {
    const title = squash(raw.replace(/^\((?:neu|new)\)\s*/i, ''));
    const segments = title.split(/\s+[-–—|]\s+/).map(squash).filter(Boolean);
    if (!segments.length) return null;

    let bio = false;
    let partner = '';
    let branded = false;
    const brand = segments[0].match(BRAND);
    if (brand) {
      branded = true;
      bio = Boolean(brand[1]);
      partner = brand[2] || '';
      segments[0] = squash(segments[0].slice(brand[0].length));
      if (!segments[0]) segments.shift();
    }
    if (!segments.length) return null;

    const setIndex = segments.findIndex((segment) => SET.test(segment));
    let kicker = '';
    let name = '';
    let rest = [];
    if (setIndex > -1) {
      let segment = segments[setIndex];
      const match = segment.match(SET_WORDS);
      let count = match[1] || '';
      segment = segment.replace(SET_WORDS, ' ');
      if (!count) {
        const loose = segment.match(COUNT);
        if (loose) {
          count = loose[1];
          segment = segment.replace(COUNT, ' ');
        }
      }
      kicker = count ? `${count}er Set` : match[2] ? `${match[2]} Set` : 'Set';
      name = squash(segment);
      rest = segments.slice(setIndex + 1);
      const before = segments.slice(0, setIndex).join(' ');
      if (!name && before) name = before;
      else if (!name && rest.length) name = rest.shift();
    } else {
      if (!branded) return null;
      name = segments[0];
      rest = segments.slice(1);
      kicker = bio ? 'TEABALLS Bio' : 'TEABALLS';
    }
    if (!name) return null;

    const kickerParts = [kicker];
    if (bio && setIndex > -1) kickerParts.push('Bio');

    let flavors = [];
    let sub = '';
    if (setIndex > -1 && rest.length) {
      flavors = rest.join(', ').split(/\s*[,•·;]\s*/).map(squash).filter(Boolean);
      if (flavors.length < 2) {
        sub = rest.join(' · ');
        flavors = [];
      }
    } else if (rest.length) {
      sub = rest.join(' · ');
    }
    return { kicker: kickerParts.join(' · ') + (partner ? ` × ${partner}` : ''), name, sub, flavors, bio, partner };
  };

  /* single-flavour products → photo; kept for a few hours in sessionStorage */
  let flavorIndex = null;
  const loadFlavors = () => {
    if (flavorIndex) return flavorIndex;
    flavorIndex = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.time < CACHE_TTL && Array.isArray(cached.items)) return cached.items;
      } catch (error) {
        /* storage unavailable */
      }
      const lists = await Promise.all(
        FLAVOR_SOURCES.map((handle) =>
          fetch(`${window.Shopify?.routes?.root || '/'}collections/${handle}/products.json?limit=250`, {
            credentials: 'same-origin',
          })
            .then((response) => (response.ok ? response.json() : { products: [] }))
            .catch(() => ({ products: [] }))
        )
      );
      const items = [];
      lists.forEach((list, sourceIndex) => {
        (list.products || []).forEach((product) => {
          const images = product.images || [];
          const art = window.TeaballsBFPricing?.flavourArt?.[product.handle];
          const image = art ? {src:art} : images.find((entry) => /(?:-icon|_icon|_ZUTAT)/i.test(entry.src));
          if (!image) return;
          const title = String(product.title || '');
          const brand = title.match(BRAND);
          const core = brand ? title.slice(brand[0].length) : title;
          items.push({
            key: keyOf(core.replace(/^\s*[-–—]\s*/, '').split(/\s+[-–—|]\s+/)[0]),
            bio: sourceIndex === 1 || Boolean(brand && brand[1]),
            partner: keyOf((brand && brand[2]) || ''),
            src: image.src,
          });
        });
      });
      if (!items.length) {
        flavorIndex = null;
        return items;
      }
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), items }));
      } catch (error) {
        /* storage unavailable */
      }
      return items;
    })();
    return flavorIndex;
  };

  const findFlavor = (items, flavor, parsed) => {
    const key = keyOf(flavor.replace(/^\d+\s*[x×]\s*/i, ''));
    if (!key) return null;
    const partner = keyOf(parsed.partner);
    let best = null;
    let bestScore = 0;
    items.forEach((item) => {
      let score = 0;
      if (item.key === key) score = 40;
      else if (item.key.startsWith(`${key} `)) score = 20;
      else if (item.key.endsWith(` ${key}`)) score = 15;
      else return;
      if (item.bio === parsed.bio) score += 4;
      if (partner && item.partner.includes(partner)) score += 30;
      if (!partner && item.partner) score -= 30;
      if (score > bestScore) {
        best = item;
        bestScore = score;
      }
    });
    return best && bestScore > 0 ? best : null;
  };

  const imageUrl = (src, width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;

  const flavorLabel = (flavor) => {
    const multiple = flavor.match(/^(\d+)\s*[x×]\s+/i);
    return (multiple ? `${multiple[1]}× ` : '') + titleCase(flavor.slice(multiple ? multiple[0].length : 0));
  };

  /**
   * Pill list shared by the quick-add modal and the product cards.
   * @param {string[]} flavors
   * @param {{ bio: boolean, partner: string }} context - set context for the photo lookup
   * @param {{ prefix: string, images: boolean, limit?: number }} options
   */
  const buildPills = (flavors, context, { prefix, images, limit = Infinity }) => {
    const list = document.createElement('ul');
    list.className = `${prefix}s__list`;
    list.setAttribute('aria-label', 'Enthaltene Sorten');
    const shown = flavors.length > limit ? flavors.slice(0, limit - 1) : flavors;
    const pills = shown.map((flavor, index) => {
      const item = document.createElement('li');
      item.className = prefix;
      item.style.setProperty('--tb-qa-i', String(index));
      if (images) {
        const media = document.createElement('span');
        media.className = `${prefix}__media`;
        media.setAttribute('aria-hidden', 'true');
        item.appendChild(media);
      } else {
        item.classList.add(`${prefix}--text`);
      }
      const label = document.createElement('span');
      label.className = `${prefix}__name`;
      label.textContent = flavorLabel(flavor);
      item.appendChild(label);
      list.appendChild(item);
      return { item, flavor };
    });
    if (shown.length < flavors.length) {
      const more = document.createElement('li');
      more.className = `${prefix} ${prefix}--more`;
      more.textContent = `+${flavors.length - shown.length}`;
      more.title = flavors.slice(shown.length).map(flavorLabel).join(', ');
      list.appendChild(more);
    }
    if (images) {
      loadFlavors()
        .then((items) => {
          pills.forEach(({ item, flavor }) => {
            const match = findFlavor(items, flavor, context);
            const media = item.querySelector(`.${prefix}__media`);
            if (!match || !media) {
              item.classList.add(`${prefix}--plain`);
              return;
            }
            const image = document.createElement('img');
            image.alt = '';
            image.decoding = 'async';
            image.loading = 'lazy';
            image.width = 72;
            image.height = 72;
            image.src = imageUrl(match.src, 144);
            image.addEventListener('load', () => media.classList.add('is-loaded'), { once: true });
            image.addEventListener('error', () => item.classList.add(`${prefix}--plain`), { once: true });
            media.appendChild(image);
          });
        })
        .catch(() => pills.forEach(({ item }) => item.classList.add(`${prefix}--plain`)));
    }
    return list;
  };

  const buildFlavors = (parsed) => {
    const wrapper = document.createElement('div');
    wrapper.className = `tb-qa-flavors tb-qa-flavors--${FLAVOR_STYLE}`;
    const heading = document.createElement('p');
    heading.className = 'tb-qa-flavors__label';
    const bottles = parsed.flavors.reduce((sum, flavor) => sum + (Number((flavor.match(/^(\d+)\s*[x×]\s+/i) || [])[1]) || 1), 0);
    heading.textContent = `Im Set · ${bottles} ${bottles === 1 ? 'Flasche' : 'Flaschen'}`;
    wrapper.append(heading, buildPills(parsed.flavors, parsed, { prefix: 'tb-qa-flavor', images: FLAVOR_STYLE === 'image' }));
    return wrapper;
  };

  const decorateTitle = (root) => {
    const link =
      root.querySelector('.view-product-title a') || root.querySelector(':scope > .product-header > a:not(product-price *)');
    if (!link || link.querySelector('.tb-qa-name')) return;
    const original = squash(link.textContent || '');
    if (!original) return;
    const parsed = parseTitle(original);
    root.querySelectorAll('.tb-qa-flavors, .tb-qa-sub').forEach((node) => node.remove());
    if (!parsed) return;

    link.setAttribute('aria-label', original);
    link.title = original;
    link.classList.add('tb-qa-title');
    const kicker = document.createElement('span');
    kicker.className = 'tb-qa-kicker';
    kicker.textContent = parsed.kicker;
    const name = document.createElement('span');
    name.className = 'tb-qa-name';
    name.textContent = parsed.name.replace(/[.!?]+$/, '');
    /* longest word, so the CSS can size the name to fit its column without breaking a word */
    const longest = Math.max(...parsed.name.split(/\s+/).map((word) => word.length), 4);
    name.style.setProperty('--tb-qa-longest', String(longest + 1));
    link.replaceChildren(kicker, name);
    link.parentElement.classList.add('tb-qa-head');

    if (parsed.sub) {
      const sub = document.createElement('p');
      sub.className = 'tb-qa-sub';
      sub.textContent = titleCase(parsed.sub);
      link.after(sub);
    }
    if (parsed.flavors.length) {
      /* desktop: inside the title block (kept by the modal's allow-list); phones: full width under the header */
      const compactHeader = link.closest('.product-header');
      const after = compactHeader || link.parentElement.querySelector(':scope > .tb-qa-sub') || link;
      after.after(buildFlavors(parsed));
    }
  };

  /* ---------- product cards: poster name + flavour pills ---------- */
  /* card text blocks, identical in every collection template (see teaballs-bm-collection.css) */
  const CARD_BLOCK = {
    vendor: '__text_WrCMch',
    title: '__text_4FQnng',
    set: '__text_8Myz4x',
    productTitle: '__product_title_4nY4eT',
    flavor: '__text_mV3TwT',
  };
  const CARD_PILL_LIMIT = 6;
  /* collection grids and Horizon product lists (BFCM hub "Deine Sorten. Direkt bestellen.") */
  const CARD_SCOPE = '.product-grid product-card, .resource-list product-card';

  /** "Himbeere & Vanille" → ["Himbeere", "Vanille"]; a single phrase stays a sub line */
  const splitPair = (text) => {
    const parts = (text || '').split(/\s*(?:&|,|\bund\b|\+)\s*/i).map(squash).filter(Boolean);
    return parts.length > 1 ? parts : [];
  };

  /** partner lines such as "SimsalaGrimm – Schneewittchens Zauberapfel" */
  const partnerTitle = (title) => {
    const segments = title.split(/\s+[-–—|]\s+/).map(squash).filter(Boolean);
    if (segments.length < 2) return null;
    return { kicker: segments[0], name: segments.slice(1).join(' '), sub: '', flavors: [], bio: false, partner: '' };
  };

  const blockText = (content, id) =>
    squash(content.querySelector(`:scope > [class*='${id}']`)?.textContent || '');

  /** "Kirsche | Wildberry" (sets) or "Fenchel, Pfefferminze und Kamille" (ingredients) */
  const splitFlavors = (text) => {
    if (!text) return [];
    const parts = text.includes('|') ? text.split('|') : text.split(/\s*,\s*|\s+und\s+|\s*•\s*/i);
    return parts.map(squash).filter(Boolean);
  };

  const decorateCard = (card) => {
    const content = card.querySelector('.product-card__content');
    if (!content || content.querySelector('.tb-card-head')) return;

    /* product-title blocks have a different id per section (e.g. the product list on the BFCM hub) */
    const titleBlock = content.querySelector("[class*='__product_title_']");
    const fullTitle = squash(
      card.querySelector('.product-grid-view-zoom-out--details')?.textContent || titleBlock?.textContent || ''
    );
    const parsed = fullTitle ? parseTitle(fullTitle) || partnerTitle(fullTitle) : null;
    const name = (
      blockText(content, CARD_BLOCK.set).replace(/\s+set$/i, '') ||
      blockText(content, CARD_BLOCK.title) ||
      (parsed && parsed.name) ||
      squash(titleBlock?.textContent || '')
    ).replace(/[.!?]+$/, '');
    if (!name) return;

    const vendor = blockText(content, CARD_BLOCK.vendor);
    const kicker = (parsed && parsed.kicker) || vendor || 'TEABALLS';
    const isSet = /\bset\b/i.test(kicker);
    const context = {
      bio: parsed ? parsed.bio : /\bbio\b/i.test(vendor),
      partner: parsed ? parsed.partner : '',
    };

    const flavorText = blockText(content, CARD_BLOCK.flavor);
    const originalFlavor = content.querySelector(`:scope > [class*='${CARD_BLOCK.flavor}']`);
    if (originalFlavor) originalFlavor.hidden = true;
    let flavors = splitFlavors(flavorText);
    let sub = '';
    if (!flavorText && parsed) {
      /* no flavour line on the card: take the flavours from the product title */
      flavors = parsed.flavors.length ? parsed.flavors : splitPair(parsed.sub);
      if (!flavors.length && parsed.sub) sub = parsed.sub;
    }
    if (flavors.length === 1) {
      /* a single entry is either the product's own name (nothing to add) or a short line */
      if (keyOf(flavors[0]) !== keyOf(name)) sub = flavors[0];
      flavors = [];
    }

    const head = document.createElement('div');
    head.className = 'tb-card-head';
    const stamp = document.createElement('span');
    stamp.className = 'tb-card-kicker';
    stamp.textContent = kicker;
    const title = document.createElement('p');
    title.className = 'tb-card-name';
    const longest = Math.max(...name.split(/\s+/).map((word) => word.length), 4);
    title.style.setProperty('--tb-qa-longest', String(longest + 1));
    title.textContent = name;
    head.append(stamp, title);
    if (sub) {
      const line = document.createElement('p');
      line.className = 'tb-card-sub';
      line.textContent = titleCase(sub);
      head.appendChild(line);
    }

    const nodes = [head];
    if (flavors.length) {
      /* set flavours get the bottle photo; ingredient lists of single flavours stay name-only */
      const images = FLAVOR_STYLE === 'image' && isSet;
      const pills = buildPills(flavors, context, { prefix: 'tb-card-flavor', images, limit: CARD_PILL_LIMIT });
      pills.classList.add(images ? 'tb-card-flavors--image' : 'tb-card-flavors--text');
      nodes.push(pills);
    }

    /* title inside the card's own link (product-list cards): the poster name stays a link */
    const titleLink = titleBlock && titleBlock.closest('.product-card__content > a');
    if (titleLink) {
      titleLink.appendChild(head);
      titleLink.after(...nodes.slice(1));
      return;
    }
    const before = content.querySelector(':scope > product-price') ||
      content.querySelector(`:scope > [class*='${CARD_BLOCK.flavor}']`);
    if (before) before.before(...nodes);
    else content.append(...nodes);
  };

  let cardsQueued = false;
  const decorateCards = () => {
    cardsQueued = false;
    document.querySelectorAll(CARD_SCOPE).forEach(decorateCard);
  };
  const queueCards = () => {
    if (cardsQueued) return;
    cardsQueued = true;
    requestAnimationFrame(decorateCards);
  };

  /* ---------- Wunschmix flavour cards ---------- */
  const decorateMixCard = (card) => {
    const heading = card.querySelector('.tb13-card-body > h3');
    const link = heading && heading.querySelector('a');
    if (!link || link.querySelector('.tb-mix-name')) return;

    const imageLabel = card.querySelector('.tb13-card-image')?.getAttribute('aria-label') || '';
    const original = squash(imageLabel.replace(/:\s*Details$/i, '') || link.textContent || '');
    if (!original) return;
    const parsed = parseTitle(original) || partnerTitle(original);
    if (!parsed) return;

    const kicker = document.createElement('span');
    kicker.className = 'tb-mix-kicker';
    kicker.textContent = parsed.kicker;
    const name = document.createElement('span');
    name.className = 'tb-mix-name';
    name.textContent = parsed.name.replace(/[.!?]+$/, '');
    const longest = Math.max(...parsed.name.split(/\s+/).map((word) => word.length), 4);
    name.style.setProperty('--tb-qa-longest', String(longest + 1));
    link.setAttribute('aria-label', original);
    link.replaceChildren(kicker, name);
    heading.classList.add('tb-mix-title');

    let last = heading;
    if (parsed.sub) {
      const parts = splitPair(parsed.sub);
      if (parts.length) {
        const pills = buildPills(parts, parsed, { prefix: 'tb-mix-flavor', images: false });
        heading.after(pills);
        last = pills;
      } else {
        const line = document.createElement('p');
        line.className = 'tb-mix-sub';
        line.textContent = titleCase(parsed.sub);
        heading.after(line);
        last = line;
      }
    }
    const bioLabel = card.querySelector('.tb13-card-body > .tb13-bio-label');
    if (bioLabel) last = bioLabel;
    /* everything below (variant, price, controls) sits at the foot of the card */
    last.classList.add('tb-mix-head-end');
  };

  const decorateMixCards = () => document.querySelectorAll('.tb13-card').forEach(decorateMixCard);

  /* Wunschmix heading → "DEIN TEE." stamp + "DEIN MIX." poster line with a spark */
  const decorateMixHeading = () => {
    const heading = document.querySelector('.tb13-intro h1');
    if (!heading || heading.querySelector('.tb-mix-h-big')) return;
    const text = squash(heading.textContent || '');
    if (!text) return;
    const split = text.match(/^(.+?[.!?])\s+(.+)$/);
    const nodes = [];
    if (split) {
      const stamp = document.createElement('span');
      stamp.className = 'tb-mix-h-stamp';
      stamp.textContent = split[1];
      nodes.push(stamp, document.createTextNode(' '));
    }
    const big = document.createElement('span');
    big.className = 'tb-mix-h-big';
    const words = document.createElement('span');
    words.className = 'tb-mix-h-words';
    words.textContent = (split ? split[2] : text).replace(/[.!?]+$/, '');
    const spark = document.createElement('span');
    spark.className = 'tb-mix-h-spark';
    spark.setAttribute('aria-hidden', 'true');
    big.append(words, spark);
    nodes.push(big);
    heading.replaceChildren(...nodes);
    heading.classList.add('tb-mix-poster');
  };

  /* ---------- product page title ---------- */
  /* flavour switcher: "TEABALLS - 3er Set FRUIT MIX - KIRSCHE, …" → "FRUIT MIX" + "3er Set" */
  const shortenSwitcher = (details) => {
    details.querySelectorAll('.fd__trigger-label, .fd__option-label').forEach((label) => {
      if (label.querySelector('.tb-fd-name')) return;
      const text = squash(label.textContent || '');
      const parsed = text && parseTitle(text);
      if (!parsed || !/\bset\b/i.test(parsed.kicker)) return;
      const name = document.createElement('span');
      name.className = 'tb-fd-name';
      name.textContent = parsed.name;
      const kicker = document.createElement('span');
      kicker.className = 'tb-fd-kicker';
      kicker.textContent = parsed.kicker;
      label.title = text;
      label.replaceChildren(name, kicker);
    });
  };

  const decoratePdp = () => {
    const details = document.querySelector("[id$='__main'] .product-information .product-details");
    if (!details) return;
    shortenSwitcher(details);
    const titleBlock = details.querySelector('.group-block-content > .group-block .group-block-content > .text-block');
    if (!titleBlock || titleBlock.querySelector('.tb-pdp-name')) return;

    const fullTitle = squash(details.querySelector('.view-product-title a')?.textContent || '');
    const parsed = (fullTitle && (parseTitle(fullTitle) || partnerTitle(fullTitle))) || null;
    let own = squash(titleBlock.textContent || '').replace(/\s+set$/i, '');
    /* templates that print the full product title ("TEABALLS - 5er Holzaufsteller") get the short name */
    if (own && BRAND.test(own)) own = (parseTitle(own) || {}).name || own;
    const name = (own || (parsed && parsed.name) || '').replace(/[.!?]+$/, '');
    if (!name) return;

    const host = titleBlock.querySelector('p') || titleBlock;
    const kicker = document.createElement('span');
    kicker.className = 'tb-pdp-kicker';
    kicker.textContent = (parsed && parsed.kicker) || 'TEABALLS';
    const title = document.createElement('span');
    title.className = 'tb-pdp-name';
    title.textContent = name;
    const longest = Math.max(...name.split(/\s+/).map((word) => word.length), 4);
    title.style.setProperty('--tb-qa-longest', String(longest + 1));
    host.replaceChildren(kicker, title);
    titleBlock.classList.add('tb-pdp-title');

    details.querySelectorAll('.tb-pdp-flavors').forEach((node) => node.remove());
    const flavors = parsed ? (parsed.flavors.length ? parsed.flavors : splitPair(parsed.sub)) : [];
    if (flavors.length > 1) {
      const isSet = /\bset\b/i.test(parsed.kicker);
      const images = FLAVOR_STYLE === 'image' && isSet;
      const wrapper = document.createElement('div');
      wrapper.className = `tb-pdp-flavors tb-pdp-flavors--${images ? 'image' : 'text'}`;
      if (isSet) {
        const label = document.createElement('p');
        label.className = 'tb-pdp-flavors__label';
        const bottles = flavors.reduce((sum, flavor) => sum + (Number((flavor.match(/^(\d+)\s*[x×]\s+/i) || [])[1]) || 1), 0);
        label.textContent = `Im Set · ${bottles} ${bottles === 1 ? 'Flasche' : 'Flaschen'}`;
        wrapper.appendChild(label);
      }
      wrapper.appendChild(buildPills(flavors, parsed, { prefix: 'tb-pdp-flavor', images }));
      titleBlock.after(wrapper);
    }
  };

  const prepare = (root) => {
    prepareDetails(root);
    repairCompact(root);
    decorateTitle(root);
  };

  const start = () => {
    /* cards only where their stylesheet is loaded (collections, BFCM hub): it hides the original blocks */
    if (document.querySelector("link[href*='teaballs-bm-collection.css']")) {
      decorateCards();
      /* filters, sorting and "load more" re-render the grid */
      const main = document.querySelector('main') || document.body;
      new MutationObserver(queueCards).observe(main, { childList: true, subtree: true });
    }
    decorateMixCards();
    decorateMixHeading();
    /* product page title only where teaballs-bm-product.css is loaded */
    if (document.querySelector("link[href*='teaballs-bm-product.css']")) {
      decoratePdp();
      const pdp = document.querySelector("[id$='__main'] .product-information .product-details");
      /* variant changes re-render parts of the details column */
      if (pdp) new MutationObserver(decoratePdp).observe(pdp, { childList: true, subtree: true });
    }

    const root = document.getElementById(MODAL_CONTENT_ID);
    if (!root) return;
    prepare(root);
    new MutationObserver(() => prepare(root)).observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
